import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Title,
  Text,
  TextInput,
  Button,
  Grid
} from '@tremor/react';
import { verifyInvitation } from './services/team-invite.service';

export function JoinPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invitation, setInvitation] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setError('Invalid invitation link');
      setLoading(false);
      return;
    }

    verifyInvitation(token)
      .then(inviteData => {
        setInvitation(inviteData);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [searchParams]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    try {
      setLoading(true);
      // TODO: Implement account creation API call
      await createAccount({
        ...formData,
        email: invitation.email,
        role: invitation.role,
        inviteToken: searchParams.get('token')
      });
      
      // Redirect to login page
      navigate('/login', { 
        replace: true,
        state: { message: 'Account created successfully. Please log in.' }
      });
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Text>Verifying invitation...</Text>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card>
          <Title>Error</Title>
          <Text className="mt-2 text-red-600">{error}</Text>
          <Button
            className="mt-4"
            variant="secondary"
            onClick={() => navigate('/login')}
          >
            Go to Login
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <Card className="w-full max-w-lg">
        <div className="text-center mb-6">
          <Title>Join Your Team</Title>
          <Text>Complete your account setup to join the admin portal</Text>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Grid numItems={1} numItemsSm={2} className="gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">First Name</label>
              <TextInput
                name="firstName"
                placeholder="John"
                value={formData.firstName}
                onChange={handleInputChange}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Last Name</label>
              <TextInput
                name="lastName"
                placeholder="Doe"
                value={formData.lastName}
                onChange={handleInputChange}
                required
              />
            </div>
          </Grid>

          {invitation?.email && (
            <div>
              <label className="block text-sm font-medium mb-2">Email</label>
              <TextInput
                value={invitation.email}
                disabled
                className="bg-gray-50"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-2">Password</label>
            <TextInput
              name="password"
              type="password"
              placeholder="••••••••"
              value={formData.password}
              onChange={handleInputChange}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Confirm Password</label>
            <TextInput
              name="confirmPassword"
              type="password"
              placeholder="••••••••"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              required
            />
          </div>

          <Button type="submit" className="w-full">
            Create Account
          </Button>

          <div className="text-center">
            <Text className="text-sm text-tremor-content-subtle">
              Already have an account?{' '}
              <button
                type="button"
                className="text-blue-600 hover:underline"
                onClick={() => navigate('/login')}
              >
                Log in
              </button>
            </Text>
          </div>
        </form>
      </Card>
    </div>
  );
}

// TODO: Implement this function based on your API
async function createAccount(data) {
  // Make API call to create the account
  console.log('Creating account:', data);
} 